import React, { useEffect, useState, useContext } from "react";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { firestore } from "../shared/FirebaseConfig";
import { UserContext } from "../shared/UserContext";
import { SIGNOUTS } from "./signoutData";
import { avatarColor, initials } from "../shared/avatar";
import { locStyle, publicLocation } from "../shared/signoutLocations";
import "./ActiveSignOuts.css";

function formatTime(ts) {
  const d = new Date(ts);
  const h = d.getHours(),
    m = d.getMinutes();
  return `${h % 12 || 12}:${m.toString().padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
}

function getElapsed(ts, now) {
  const mins = Math.floor((now - new Date(ts)) / 60000);
  if (mins < 1) return "just now";
  if (mins === 1) return "1 min";
  return `${mins} min`;
}

// Normalize old { uid, displayName } objects or plain UID strings
function toUid(entry) {
  return typeof entry === "string" ? entry : entry?.uid;
}

export function FollowedSignOuts() {
  const { user, schoolId } = useContext(UserContext);
  const [following, setFollowing] = useState([]); // array of UIDs
  const [signOutData, setSignOutData] = useState([]);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 15000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!user?.uid) return;
    return onSnapshot(doc(firestore, "users", user.uid), (snap) => {
      const raw = snap.data()?.following || [];
      const next = raw.map(toUid).filter(Boolean);
      setFollowing((prev) => {
        const same =
          prev.length === next.length && prev.every((u, i) => u === next[i]);
        return same ? prev : next;
      });
    });
  }, [user?.uid]);

  useEffect(() => {
    if (!following.length || !schoolId) {
      setSignOutData([]);
      return;
    }

    // One listener per followed teacher — their active sign-outs carry className
    // and teacherName ("Ms. Mack") directly on the record. schoolId scopes each query
    // so the security rules authorize reading a same-school colleague's sign-outs.
    const unsubs = following.map((teacherUid) =>
      onSnapshot(
        query(
          collection(firestore, SIGNOUTS),
          where("schoolId", "==", schoolId),
          where("teacherUID", "==", teacherUid),
        ),
        (snap) => {
          const rows = snap.docs.map((d) => ({
            id: d.id,
            teacherUid,
            ...d.data(),
          }));
          setSignOutData((prev) => [
            ...prev.filter((s) => s.teacherUid !== teacherUid),
            ...rows,
          ]);
        },
      ),
    );

    return () => {
      unsubs.forEach((fn) => fn());
      setSignOutData([]);
    };
  }, [following, schoolId]);

  const sorted = [...signOutData].sort(
    (a, b) => new Date(a.time_out) - new Date(b.time_out),
  );

  if (!following.length || sorted.length === 0) return null;

  return (
    <div className="display-sign-out-container" style={{ marginTop: 32 }}>
      <div className="signout-header">
        <h2 style={{ margin: 0 }}>
          Other Classes
          {signOutData.length > 0 && (
            <span className="signout-count">{signOutData.length} out</span>
          )}
        </h2>
      </div>

      <div className="signout-list">
        {sorted.map((s) => {
          const mins = Math.floor((now - new Date(s.time_out)) / 60000);
          const isLong = mins >= 10;
          // Followed boards are awareness-only, so a student's typed "Other"
          // destination collapses back to "Other" (never broadcast across classes).
          const displayLoc = publicLocation(s.location);
          const ls = locStyle(displayLoc);
          // Teacher name gets its own prominent pill (below), so keep it out of the meta.
          const meta = [
            s.homeroom,
            s.room ? `Room ${s.room}` : null,
            `Left ${formatTime(s.time_out)}`,
          ]
            .filter(Boolean)
            .join(" · ");
          const teacherTint = s.teacherName ? avatarColor(s.teacherName) : null;
          return (
            <div
              key={`${s.classId}-${s.id}`}
              className={`signout-card ${isLong ? "signout-card--long" : ""}`}
            >
              <span
                className="signout-avatar"
                style={{ background: avatarColor(s.studentName) }}
              >
                {initials(s.studentName)}
              </span>
              <span className="signout-body">
                <span className="signout-name">{s.studentName}</span>
                <span className="signout-sub">{meta}</span>
              </span>
              {s.teacherName && (
                <span
                  className="followed-teacher-pill"
                  style={{
                    background: `${teacherTint}22`,
                    color: teacherTint,
                    border: `1px solid ${teacherTint}66`,
                    fontWeight: 600,
                    fontSize: 13,
                    padding: "3px 10px",
                    borderRadius: 999,
                    whiteSpace: "nowrap",
                  }}
                >
                  {s.teacherName}
                </span>
              )}
              <span
                className="signout-loc"
                style={{ background: ls.bg, color: ls.color }}
              >
                {ls.emoji} {displayLoc}
              </span>
              <span className={`signout-time ${isLong ? "is-long" : ""}`}>
                {getElapsed(s.time_out, now)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// A teacher's shown name: prefer "Mr. Mack" (title + last), else displayName.
function teacherLabel(t) {
  return (
    [t.title, t.lastName].filter(Boolean).join(" ").trim() ||
    t.displayName ||
    t.email ||
    "Unknown Teacher"
  );
}

export function ManageFollowing() {
  const { user, schoolId } = useContext(UserContext);
  const [following, setFollowing] = useState([]); // array of UIDs
  const [followingNames, setFollowingNames] = useState({}); // { uid: displayName }
  const [schoolTeachers, setSchoolTeachers] = useState([]); // same-school users
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user?.uid) return;
    return onSnapshot(doc(firestore, "users", user.uid), (snap) => {
      const raw = snap.data()?.following || [];
      setFollowing(raw.map(toUid).filter(Boolean));
    });
  }, [user?.uid]);

  // Load same-school teachers once (the rules authorize a schoolId-scoped read; an
  // email-equality query is not authorized). We filter this list live, client-side.
  useEffect(() => {
    if (!schoolId) return;
    getDocs(
      query(collection(firestore, "users"), where("schoolId", "==", schoolId)),
    )
      .then((snap) =>
        setSchoolTeachers(
          snap.docs
            .map((d) => d.data())
            .filter((u) => u.uid && u.uid !== user?.uid),
        ),
      )
      .catch(() => setError("Couldn't load your school's teachers."));
  }, [schoolId, user?.uid]);

  // Fetch display names for any UIDs we don't have yet
  useEffect(() => {
    const unknown = following.filter((uid) => !followingNames[uid]);
    if (!unknown.length) return;
    Promise.all(
      unknown.map((uid) =>
        getDoc(doc(firestore, "users", uid)).then((snap) => ({
          uid,
          displayName: snap.data()?.displayName || "Unknown Teacher",
        })),
      ),
    ).then((results) => {
      setFollowingNames((prev) => {
        const next = { ...prev };
        results.forEach((r) => {
          next[r.uid] = r.displayName;
        });
        return next;
      });
    });
  }, [following]); // eslint-disable-line react-hooks/exhaustive-deps

  const follow = async (teacher) => {
    await updateDoc(doc(firestore, "users", user.uid), {
      following: arrayUnion(teacher.uid),
    });
    setFollowingNames((prev) => ({
      ...prev,
      [teacher.uid]: teacherLabel(teacher),
    }));
    setSearch("");
  };

  const unfollow = async (uid) => {
    await updateDoc(doc(firestore, "users", user.uid), {
      following: arrayRemove(uid),
    });
  };

  // Live, client-side filter over same-school teachers (by name or email).
  const q = search.trim().toLowerCase();
  const matches = schoolTeachers.filter((t) => {
    if (!q) return false; // only show results once the user types
    return (
      teacherLabel(t).toLowerCase().includes(q) ||
      (t.email || "").toLowerCase().includes(q)
    );
  });

  return (
    <div
      style={{ marginTop: 32, paddingTop: 24, borderTop: "1px solid #f0f0f0" }}
    >
      <h3 style={{ margin: "0 0 12px", fontSize: 16 }}>Follow a Teacher</h3>
      <input
        type="text"
        placeholder="Search teachers by name or email"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        style={{ width: "100%", boxSizing: "border-box", marginBottom: 12 }}
      />
      {error && (
        <p style={{ color: "red", margin: "0 0 12px", fontSize: 14 }}>
          {error}
        </p>
      )}
      {q && matches.length === 0 && (
        <p
          style={{
            color: "var(--color-secondary)",
            margin: "0 0 12px",
            fontSize: 14,
          }}
        >
          No teachers at your school match “{search}”.
        </p>
      )}
      {matches.map((t) => (
        <div
          key={t.uid}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            marginBottom: 8,
            padding: "10px 16px",
            background: "#f9f9f9",
            border: "1px solid #e0e0e0",
            borderRadius: 8,
          }}
        >
          <span style={{ flex: 1, fontSize: 14 }}>
            {teacherLabel(t)}{" "}
            {t.email && (
              <span style={{ color: "var(--color-secondary)" }}>
                ({t.email})
              </span>
            )}
          </span>
          {following.includes(t.uid) ? (
            <span style={{ fontSize: 13, color: "var(--color-secondary)" }}>
              Already following
            </span>
          ) : (
            <button onClick={() => follow(t)}>Follow</button>
          )}
        </div>
      ))}
      {following.length > 0 && (
        <div>
          <p
            style={{
              margin: "0 0 8px",
              fontSize: 13,
              color: "var(--color-secondary)",
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Currently following
          </p>
          {following.map((uid) => (
            <div
              key={uid}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "8px 0",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <span style={{ flex: 1, fontSize: 14 }}>
                {followingNames[uid] || "…"}
              </span>
              <button
                onClick={() => unfollow(uid)}
                style={{
                  background: "none",
                  border: "1px solid #ddd",
                  color: "var(--color-secondary)",
                  padding: "4px 12px",
                  fontSize: 13,
                  cursor: "pointer",
                  borderRadius: 4,
                }}
              >
                Unfollow
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
